import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '../../Utilities/TruncatedTableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APIContext } from '../../Contexts/APIContext';
import { ReleaseVersion } from './ReleaseManagementTypes';

interface Props {
    versions: ReleaseVersion[];
    revision: number;
}

type NewSite = {
    reference: string;
    description: string;
    townCity: string;
    surveyType: string;
    mostRecentSurvey: string | null;
    versionId: number;
    versionName: string;
};

type NewSitesResponse = { selectedVersion: ReleaseVersion; sites: NewSite[] };

const displayDate = (value: string | null) =>
    value ? new Date(value).toLocaleDateString('en-GB') : '—';

const csvCell = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;

export default function NewSitesByVersionPanel({ versions, revision }: Props) {
    const { get, setVersion } = useContext(APIContext);
    const navigate = useNavigate();
    const [versionId, setVersionId] = useState(0);
    const [result, setResult] = useState<NewSitesResponse | null>(null);
    const orderedVersions = useMemo(
        () => [...versions].sort((left, right) =>
            new Date(right.start).getTime() - new Date(left.start).getTime() ||
            right.id - left.id),
        [versions]
    );
    const comparableVersions = useMemo(
        () => orderedVersions.slice(0, -1),
        [orderedVersions]
    );

    useEffect(() => {
        if (comparableVersions.length === 0) return;
        if (!comparableVersions.some(item => item.id === versionId))
            setVersionId(comparableVersions[0].id);
    }, [comparableVersions, versionId]);

    useEffect(() => {
        if (!versionId) return;
        setResult(null);
        get('Admin', `GetNewSitesByVersion?versionId=${versionId}`, setResult);
    }, [versionId, revision]);

    const openSite = (site: NewSite) => {
        setVersion(site.versionId);
        const params = new URLSearchParams({
            siteRef: site.reference,
            returnTo: '/admin/superadmin',
            returnLabel: 'Back to Super Admin'
        });
        navigate(`/site?${params.toString()}`);
    };

    const exportCsv = () => {
        if (!result) return;

        const rows = [
            ['Reference', 'Description', 'Town / City', 'Survey type', 'Latest survey', 'Data version'],
            ...result.sites.map(site => [
                site.reference,
                site.description,
                site.townCity,
                site.surveyType,
                site.mostRecentSurvey ? displayDate(site.mostRecentSurvey) : '',
                site.versionName
            ])
        ];
        const csv = rows.map(row => row.map(csvCell).join(',')).join('\r\n');
        const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `new-sites-${result.selectedVersion.versionName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return <Paper
        variant="outlined"
        sx={{ height: '100%', overflow: 'hidden', borderRadius: 3 }}
    >
        <Box sx={{ p: { xs: 2, md: 2.5 }, borderBottom: 1, borderColor: 'divider' }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                gap={2}
            >
                <Box>
                    <Typography variant="h6">New sites by release</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Browse sites that first appeared in a selected data version.
                    </Typography>
                </Box>
                <TextField
                    select
                    size="small"
                    label="Data version"
                    value={versionId}
                    onChange={event => setVersionId(Number(event.target.value))}
                    sx={{ minWidth: 220 }}
                >
                    {comparableVersions.map(version => <MenuItem key={version.id} value={version.id}>
                        {version.versionName}
                    </MenuItem>)}
                </TextField>
            </Stack>
        </Box>

        {!result ? <Box sx={{ minHeight: 240, display: 'grid', placeItems: 'center' }}>
            <CircularProgress size={28} />
        </Box> : <Box sx={{ p: { xs: 2, md: 2.5 }, pt: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                        size="small"
                        color="primary"
                        label={`${result.sites.length} new site${result.sites.length === 1 ? '' : 's'}`}
                    />
                    <Typography variant="body2" color="text.secondary">
                        in {result.selectedVersion.versionName}
                    </Typography>
                </Stack>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadOutlinedIcon />}
                    onClick={exportCsv}
                    disabled={result.sites.length === 0}
                >
                    Export CSV
                </Button>
            </Stack>
            {result.sites.length === 0 ? <Alert severity="info">
                No new sites were found for this version.
            </Alert> : <TableContainer sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small" sx={{ minWidth: 900, tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 150 }}>Reference</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell sx={{ width: 220 }}>Town / City</TableCell>
                            <TableCell sx={{ width: 150, whiteSpace: 'nowrap' }}>Survey type</TableCell>
                            <TableCell sx={{ width: 140 }}>Latest survey</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {result.sites.map(site => <TableRow key={site.reference} hover>
                            <TableCell>
                                <Link
                                    component="button"
                                    type="button"
                                    underline="hover"
                                    onClick={() => openSite(site)}
                                    sx={{ display: 'inline-flex', alignItems: 'center', fontWeight: 600, whiteSpace: 'nowrap' }}
                                >
                                    {site.reference}
                                </Link>
                            </TableCell>
                            <TableCell sx={{ overflow: 'hidden' }}>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {site.description || '—'}
                                </Box>
                            </TableCell>
                            <TableCell sx={{ overflow: 'hidden' }}>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {site.townCity || '—'}
                                </Box>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{site.surveyType || '—'}</TableCell>
                            <TableCell>{displayDate(site.mostRecentSurvey)}</TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>}
        </Box>}
    </Paper>;
}
